// Conversation AI service - handles natural dialogue with the examiner

// Simple conversation for non-assessment contexts (login, face enrollment, etc.)
export async function handleGeneralConversation(userInput: string, learnerName?: string): Promise<string> {
  const lower = userInput.toLowerCase().trim()
  const name = learnerName || 'there'

  // Common patterns - keep responses SHORT (under 15 words)
  if (/^(hi|hello|hey|good morning|good afternoon|good evening)\b/i.test(lower)) {
    return `Hello ${name}! Follow the on-screen instructions.`
  }
  if (/who are you|what('s| is) your name/i.test(lower)) {
    return "I'm Stella, your assessment guide."
  }
  if (/how are you/i.test(lower)) {
    return "I'm great, thanks! Let's continue."
  }
  if (/what (is|are) (this|we doing)|what's going on/i.test(lower)) {
    return "This is a Digital Fluency Assessment."
  }
  if (/help|what (should|do) i do/i.test(lower)) {
    return "Follow the steps on screen."
  }
  if (/thank|thanks/i.test(lower)) {
    return "You're welcome!"
  }
  if (/^(ok|okay|sure|alright|got it|i see|right)\s*[.!?]*$/i.test(lower)) {
    return "Great, continue please."
  }

  // Default response
  return `Got it. Please continue with the current step.`
}

export interface ConversationIntent {
  type: 'answer' | 'conversation'
  response?: string // AI response for conversation
  extractedAnswer?: string // For answers, the text to record
}

export interface ConversationContext {
  currentQuestion: string
  questionType: 'mcq' | 'maq' | 'short-response' | 'scenario' | 'whiteboard'
  options?: string[]
  questionNumber: number
  totalQuestions: number
  learnerName: string
  followUp?: string
  isFollowUp?: boolean
}

// Cloudflare AI endpoint - calls our worker which proxies to Workers AI
const AI_WORKER_URL = 'https://really-assess.techtree.workers.dev/api/chat'

// Analyze user input and generate appropriate response
export async function analyzeConversation(
  userInput: string,
  context: ConversationContext
): Promise<ConversationIntent> {
  const trimmedInput = userInput.trim()

  // Empty input - prompt for answer
  if (!trimmedInput) {
    return {
      type: 'conversation',
      response: "I didn't catch that. Could you please respond to the question?",
    }
  }

  // Very short single letters for MCQ - definitely an answer
  if (context.questionType === 'mcq' && /^[a-d]\.?$/i.test(trimmedInput)) {
    const letterIndex = trimmedInput.toLowerCase().charCodeAt(0) - 'a'.charCodeAt(0)
    if (context.options && letterIndex < context.options.length) {
      return {
        type: 'answer',
        extractedAnswer: context.options[letterIndex],
      }
    }
  }

  // Try to call AI for smart analysis
  try {
    const result = await callConversationAI(trimmedInput, context)
    return result
  } catch {
    // Fallback: use simple classifier (conservative - asks when unsure)
    return classifySimple(trimmedInput, context)
  }
}

// Call the AI worker for conversation analysis
async function callConversationAI(
  userInput: string,
  context: ConversationContext
): Promise<ConversationIntent> {
  const systemPrompt = buildSystemPrompt(context)

  const response = await fetch(AI_WORKER_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userInput },
      ],
      context: {
        question: context.currentQuestion,
        questionNumber: context.questionNumber,
        totalQuestions: context.totalQuestions,
        questionType: context.questionType,
        options: context.options,
        learnerName: context.learnerName,
      },
    }),
  })

  if (!response.ok) {
    throw new Error(`AI API error: ${response.status}`)
  }

  const data = await response.json() as {
    type: 'answer' | 'conversation'
    response?: string
    extractedAnswer?: string
  }

  return {
    type: data.type || 'conversation', // Default to conversation (ask) when unsure
    response: data.response,
    extractedAnswer: data.extractedAnswer || userInput,
  }
}

function buildSystemPrompt(context: ConversationContext): string {
  const questionInfo = context.isFollowUp
    ? `Follow-up question: "${context.followUp}"`
    : `Question ${context.questionNumber} of ${context.totalQuestions}: "${context.currentQuestion}"`

  const optionsInfo = context.options
    ? `\nOptions: ${context.options.map((o, i) => `${String.fromCharCode(65 + i)}) ${o}`).join(', ')}`
    : ''

  return `You are Stella, a warm and professional AI examiner conducting a Digital Fluency Assessment for ${context.learnerName}.

Current ${questionInfo}
Question type: ${context.questionType}${optionsInfo}

Your task: Determine if the learner's message is a CLEAR ANSWER to the question.

A message is an ANSWER only if it:
- Directly addresses the question being asked
- For MCQ: mentions an option letter (A/B/C/D) or clearly states one of the options
- For open questions: provides substantive content clearly related to the topic
- Shows clear intent to answer (not just thinking aloud)

A message is CONVERSATION (not an answer) if:
- It's a greeting, question, or request
- It shows confusion or asks for clarification
- It's thinking aloud ("hmm", "let me think", "I'm not sure")
- It's off-topic or unrelated to the question
- It's too vague or short to be a meaningful answer
- You're NOT SURE if it's meant as an answer

IMPORTANT: When in doubt, classify as CONVERSATION and ask for clarification. It's better to ask than to submit something that wasn't meant as an answer.

Respond in JSON format only:
{"type": "answer" | "conversation", "response": "your response if conversation", "extractedAnswer": "the answer text if answer"}

Examples:
- "B" for MCQ → {"type": "answer", "extractedAnswer": "B"}
- "Password manager stores passwords securely" → {"type": "answer", "extractedAnswer": "Password manager stores passwords securely"}
- "um" → {"type": "conversation", "response": "Take your time. What's your answer?"}
- "hello" → {"type": "conversation", "response": "Hello! Let's focus on the question: ${context.currentQuestion}"}
- "something like that" → {"type": "conversation", "response": "Could you be more specific? Is that your answer to the question?"}
- "I guess" → {"type": "conversation", "response": "Would you like to give that as your answer? Please tell me what you think."}
- "yeah" → {"type": "conversation", "response": "Is that your final answer? Please elaborate."}
- "maybe security" → {"type": "conversation", "response": "Are you saying your answer is about security? Please give me your complete answer."}`
}

// Simple fallback classifier when AI is unavailable - CONSERVATIVE: asks when unsure
export function classifySimple(input: string, context: ConversationContext): ConversationIntent {
  const lower = input.toLowerCase().trim()
  const question = context.currentQuestion

  // MCQ letter answers - clear answer
  if (context.questionType === 'mcq' && context.options) {
    const letterMatch = lower.match(/^([a-d])\.?$/i)
    if (letterMatch) {
      const index = letterMatch[1].charCodeAt(0) - 'a'.charCodeAt(0)
      if (index < context.options.length) {
        return { type: 'answer', extractedAnswer: context.options[index] }
      }
    }

    // Check if they said the full option text
    for (const option of context.options) {
      if (lower === option.toLowerCase() || lower.includes(option.toLowerCase())) {
        return { type: 'answer', extractedAnswer: option }
      }
    }
  }

  // Clear conversation patterns - definitely not answers
  const conversationPatterns = [
    { pattern: /^(hi|hello|hey|good morning|good afternoon|good evening)\b/i, response: `Hello ${context.learnerName}! Let's focus on the question: ${question}` },
    { pattern: /repeat|again|what was|say that|come again/i, response: `Sure! The question is: ${question}` },
    { pattern: /don't understand|confused|unclear|what do you mean/i, response: `Let me rephrase: ${question}. Take your time to think about it.` },
    { pattern: /^(um+|uh+|hmm+|err+|ah+)\s*$/i, response: "Take your time. What's your answer?" },
    { pattern: /let me think|give me a (moment|second|minute)|hold on|wait|thinking/i, response: "Of course, take your time. Let me know your answer when ready." },
    { pattern: /i('m| am) not sure|don't know|no idea|no clue/i, response: "That's okay. Just share your best thinking - what do you think the answer might be?" },
    { pattern: /can i skip|next question|pass|move on/i, response: "I'd encourage you to give it a try. What's your initial thought?" },
    { pattern: /hint|help me|clue|assist/i, response: "I can't give hints, but I can clarify the question if needed. What part is unclear?" },
    { pattern: /how (many|long|much time)/i, response: `You're on question ${context.questionNumber} of ${context.totalQuestions}. Take the time you need.` },
    { pattern: /thank|thanks/i, response: "You're welcome! Now, what's your answer to the question?" },
    { pattern: /^what\s*\??$/i, response: `The question is: ${question}` },
    { pattern: /sorry|pardon|excuse me/i, response: `No problem! Here's the question again: ${question}` },
    { pattern: /nervous|anxious|scared|worried/i, response: "It's completely normal to feel that way. Take a deep breath. What's your answer?" },
    { pattern: /who are you|what('s| is) your name/i, response: "I'm Stella, your AI assessment guide. Now, what's your answer to the question?" },
    { pattern: /how are you/i, response: `I'm doing well, thank you! Let's focus on the question: ${question}` },
    { pattern: /can you explain/i, response: `I can repeat the question: ${question}. What's your answer?` },
  ]

  for (const { pattern, response } of conversationPatterns) {
    if (pattern.test(lower)) {
      return { type: 'conversation', response }
    }
  }

  // Short vague responses - ask for clarification
  const vaguePatterns = [
    { pattern: /^(ok|okay|sure|alright|got it|i see|right|yes|no|yeah|yep|nope|nah|maybe|possibly|probably|i guess|i think so|kind of|sort of)\s*[.!?]*$/i,
      response: "Is that your answer to the question? Please give me a complete response." },
    { pattern: /^(something|anything|nothing|stuff|things)\s*(like that|else)?\s*$/i,
      response: "Could you be more specific? What's your actual answer?" },
    { pattern: /^i (think|believe|guess|suppose)\s*$/i,
      response: "What do you think? Please share your answer." },
  ]

  for (const { pattern, response } of vaguePatterns) {
    if (pattern.test(lower)) {
      return { type: 'conversation', response }
    }
  }

  // Very short input (1-2 words, less than 15 chars) - probably not a complete answer, ask
  const wordCount = lower.split(/\s+/).length
  if (wordCount <= 2 && lower.length < 15) {
    // Exception: if it matches part of an MCQ option, might be valid
    if (context.options) {
      for (const option of context.options) {
        if (option.toLowerCase().includes(lower) && lower.length > 3) {
          return {
            type: 'conversation',
            response: `Did you mean "${option}"? Please confirm your answer.`
          }
        }
      }
    }
    return {
      type: 'conversation',
      response: `I heard "${input}". Is that your complete answer, or would you like to elaborate?`
    }
  }

  // Ends with question mark - they're asking something
  if (lower.endsWith('?')) {
    return {
      type: 'conversation',
      response: `To answer your question: Let's focus on the assessment. The question is: ${question}`
    }
  }

  // If it's a reasonably long response (3+ words, 20+ chars) that doesn't match conversation patterns,
  // it's probably an answer
  if (wordCount >= 3 && lower.length >= 20) {
    return { type: 'answer', extractedAnswer: input }
  }

  // Default: when unsure, ASK for clarification
  return {
    type: 'conversation',
    response: `I want to make sure I understand. Is "${input}" your answer to the question?`
  }
}
